/**
 * \file App.vue
 * \brief NGWI main application vue
 *
 * Copyright(C) CRYSBERG A/S 2020
 *
 */

<template>
  <div
    class="applicationContent"
    :class="[computedPagePathClass, computedWheelStatus]"
  >
    <div
      v-if="!DoNotShowWheel && computedGetPermissions.ngwi"
      class="overlay"
      :class="{'overlay--active': computedShowOverlay}"
    >
      <div class="overlay__spinner" />
      <div class="overlay__text">
        <font-awesome-icon
          v-if="ConnectionInformation.icon && ConnectionInformation.code"
          :title="$t(`messages.header.${ConnectionInformation.code}`)"
          :icon="['custom', ConnectionInformation.icon]"
        />
        <font-awesome-icon
          v-else-if="ConnectionInformation.code"
          :title="$t(`messages.header.${ConnectionInformation.code}`)"
          :icon="['custom', 'connection-connecting']"
        />
        <br>
        <span
          v-if="ConnectionInformation.message"
          v-html="$t(`messages.header.${ConnectionInformation.code}`)"
        />
        <span v-else>
          {{ $t('messages.sharedTexts.status.loading') }}
        </span>
        <br>
      </div>
    </div>
    <header-component
      :logged-in = "LoggedIn"
      :do-not-show-wheel = "DoNotShowWheel"
      :socket-message = "SocketMessage"
      :page-message = "PageMessage"
      :dash-open-close = "DashOpenClose"
      :selected-device = "SelectedDevice"
      :connection-information = "ConnectionInformation"
      :progress = "Progress"
      :store="store"
      :rain-shut-down = "computedRainShutDown"
      :rain-shut-down-started = "rainShutDownStarted"
      @SetPageMessage = "SetPageMessage"
      @ShowModal = "ShowModal"
    />
    <div
      id="Main"
      class="main"
    >
      <div
        v-if="computedGetPermissions.wheel"
        v-show="IsWheelShown"
        id="wheel"
        @click="wheelClick"
      >
        <rain-shut-down-control
          v-if="LoggedIn"
          :rain-shut-down = "computedRainShutDown"
          :rain-shut-down-started = "rainShutDownStarted"
          :controller-group="store.getData('controller.group')"
          :controller-mode="store.getData('controller.mode')"
          :show-rain-shut-down-spinner = "store.getData('rainShutDown.showRainShutDownSpinner')"
          :wheel-state = "true"
          @SetPageMessage = "SetPageMessage"
        />
        <!-- eslint-disable -->
        <svg
          class="front"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            id="wheelRoot"
            width="100%"
            height="100%"
            viewBox="0 0 380 380"
            preserveAspectRatio="xMidYMid meet">            
          <image x="0" y="0" width="380" height="380" xlink:href="@graphics/wheel/wheelMay2019.svg" preserveAspectRatio="xMidYMid meet" id="wheelImage"/>
          <polygon data-url="history" style="stroke-width: 0px; fill-opacity: 0;" points="144.4 145.4 57.3 59.6 39.5 80.9 26.8 100.1 17.4 120.1 10.3 139.3 6.4 159.0 5.5 159.0 4.5 179.7 3.8 199.2 5.8 218.6 10.6 238.7 17.1 259.1 26.8 279.2 38.8 299.6 56.3 318.1 143.5 231.3 133.1 217.6 126.9 196.6 127.3 177.8 135.4 157.1 135.4 156.7">
            <title>{{ $t('messages.app.wheel.history.help') }}</title>
          </polygon>
          <g transform="translate(65, 150)">
            <g class="svgCircle">
              <path d="M57.31 27.13A23.67 23.67 0 0 1 33.64 50.8 23.67 23.67 0 0 1 9.97 27.13 23.67 23.67 0 0 1 33.64 3.46a23.67 23.67 0 0 1 23.67 23.67z"  fill="#fff"/>
            </g>
            <path d="M34.34 39.43a1.36 1.36 0 0 1 0-2.72 10.62 10.62 0 1 0-9.78-6.43 1.36 1.36 0 1 1-2.49 1.07A13.22 13.22 0 0 1 21 26.08a13.35 13.35 0 1 1 13.34 13.35z" />
            <path d="M34.41 29.67a1.65 1.65 0 0 1-1.41-.74l-3.65-5.5a1.64 1.64 0 1 1 2.74-1.82l2.74 4.12L37 24.31a1.65 1.65 0 1 1 1.83 2.74l-3.52 2.34a1.62 1.62 0 0 1-.9.28z" />
            <path d="M24 34.08a1.26 1.26 0 0 1-.74-.24L20 31.46a1.25 1.25 0 1 1 1.47-2l1.69 1.23.41-2.1A1.25 1.25 0 1 1 26 29l-.78 4a1.3 1.3 0 0 1-.77.93 1.48 1.48 0 0 1-.45.15z" />
            <g transform="translate(10, 10)">
              <text x="25" y="55" text-anchor="middle">{{ $t('messages.app.wheel.history.title') }}</text>
            </g>
          </g>
          <polygon data-url="weather" style="stroke-width: 0px; fill-opacity: 0;" points="232.6 142.2 318.1 57.6 298.6 39.8 299.0 39.5 279.5 27.5 279.8 27.5 259.1 17.4 239.4 11.3 220.2 6.8 199.8 4.2 199.8 3.8 181.4 4.2 161.0 6.1 140.2 11.0 121.1 17.1 100.1 26.8 80.9 39.5 61.2 55.3 147.4 142.2 153.2 138.3 153.5 137.6 164.5 132.1 177.8 127.6 193.4 126.9 207.6 129.5 220.2 134.1">
            <title>{{ $t('messages.app.wheel.weather.help') }}</title>
          </polygon>
          <g transform="translate(160, 60)" opacity=".12">
            <g class="svgCircle">
              <path d="M57.25 26.89a23.67 23.67 0 0 1-23.67 23.67A23.67 23.67 0 0 1 9.91 26.89 23.67 23.67 0 0 1 33.58 3.22a23.67 23.67 0 0 1 23.67 23.67z"  fill="#fff"/>
            </g>
            <path d="M42.25 22.43h-.37a5.75 5.75 0 0 0-.63-1.75 5.62 5.62 0 0 0-5.11-3 5 5 0 0 0-2.37.5 5.46 5.46 0 0 0-4.11-1.75 5.68 5.68 0 0 0-5.24 3 16.68 16.68 0 0 0-.63 1.75 5.08 5.08 0 0 0-4.11 5 4.63 4.63 0 0 0 2.12 4 1.13 1.13 0 0 0 1.74-.25 1.25 1.25 0 0 0-.37-1.75 2.59 2.59 0 0 1-1-2 2.49 2.49 0 0 1 2.75-2.49 1.18 1.18 0 0 0 1.24-1.25 3.45 3.45 0 0 1 3.5-3.49 3.78 3.78 0 0 1 2.11.62 3.06 3.06 0 0 0-.77 1.11 6.28 6.28 0 0 0-.62 1.75 5 5 0 0 0-2.12 9 1.25 1.25 0 1 0 1.5-2 2.6 2.6 0 0 1-1-2 2.5 2.5 0 0 1 2.74-2.5 1.44 1.44 0 0 0 1.24-1.24c0-.38.13-3.5 3.37-3.5a3.15 3.15 0 0 1 3 1.62 4.34 4.34 0 0 1 .49 1.88 1.24 1.24 0 0 0 1.25 1.12h1.37A2.44 2.44 0 0 1 45 27.29a2.34 2.34 0 0 1-2 2.49 1.26 1.26 0 0 0 .25 2.5h.25a5 5 0 0 0 4-5c0-2.87-2.12-4.86-5.24-4.86zm-1.37 5.24c-.62-.25-1.12.12-1.37.74l-1.75 6.49a1.21 1.21 0 0 0 .87 1.37h.25a1 1 0 0 0 1-.87l1.75-6.48a.92.92 0 0 0-.75-1.25zm-4.37 2.49a1.18 1.18 0 0 0-1.37.75l-1.75 6.48a1.09 1.09 0 0 0 .75 1.38h.25a1.1 1.1 0 0 0 1.13-.77l1.74-6.48a1.07 1.07 0 0 0-.75-1.37zm-3-2.49a1 1 0 0 0-1.37.74L30.4 34.9a1.08 1.08 0 0 0 .75 1.37h.25a1.07 1.07 0 0 0 1.12-.75L34.27 29a1.17 1.17 0 0 0-.75-1.37z"  fill-rule="evenodd" fill="#fff"/>
            <g transform="translate(10, 10)">
              <text x="25" y="55" text-anchor="middle">{{ $t('messages.app.wheel.weather.title') }}</text>
            </g>
          </g>
          <polygon data-url="programs" style="stroke-width: 0px; fill-opacity: 0;" points="237.7 231.6 322.9 317.4 340.1 298.0 351.8 280.2 361.8 258.1 368.0 238.7 372.5 219.6 375.1 199.5 374.8 180.4 372.2 159.3 368.3 139.3 360.5 118.5 350.8 99.1 337.8 77.7 322.6 60.5 236.4 146.1 245.2 157.4 251.0 174.6 254.3 190.4 249.4 209.2 244.5 223.2">
            <title>{{ $t('messages.app.wheel.programs.help') }}</title>
          </polygon>
          <g transform="translate(250, 150)">
            <g class="svgCircle">
              <path d="M57.31 27.13A23.67 23.67 0 0 1 33.64 50.8 23.67 23.67 0 0 1 9.97 27.13 23.67 23.67 0 0 1 33.64 3.46a23.67 23.67 0 0 1 23.67 23.67z"  fill="#fff"/>
            </g>
            <path d="M37.94 38.47a9.92 9.92 0 1 1 9.91-9.92 9.93 9.93 0 0 1-9.91 9.92zm0-17.55a7.63 7.63 0 1 0 7.63 7.63 7.64 7.64 0 0 0-7.63-7.63z" />
            <path d="M39 31.7a1.24 1.24 0 0 1-1-.64l-2.8-4.77a1.56 1.56 0 0 1 .35-2 1.18 1.18 0 0 1 1.75.4l2.11 3.58L41 27a1.19 1.19 0 0 1 1.76.4 1.57 1.57 0 0 1-.36 2l-2.7 2a1.16 1.16 0 0 1-.7.3z" />
            <path d="M28.34 31.2h-8.63a1.43 1.43 0 0 1 0-2.86h8.63a1.43 1.43 0 0 1 0 2.86z" />
            <path d="M29.25 26.93h-9.54a1.43 1.43 0 0 1 0-2.86h9.54a1.43 1.43 0 0 1 0 2.86z" />
            <path d="M32.3 23H19.71a1.43 1.43 0 0 1 0-2.86H32.3a1.43 1.43 0 0 1 0 2.86z" />
            <g transform="translate(10, 10)">
              <text x="25" y="55" text-anchor="middle">{{ $t('messages.app.wheel.programs.title') }}</text>
            </g>
          </g>
          <polygon data-url="stations" style="stroke-width: 0px; fill-opacity: 0;" points="146.1 235.8 59.9 322.0 80.9 341.1 99.4 352.1 119.2 361.5 140.2 368.6 140.2 368.3 159.7 372.5 178.8 374.4 197.9 374.8 218.9 372.2 240.3 368.0 259.1 360.8 280.2 351.8 301.2 337.8 320.0 322.0 234.8 235.5 224.1 243.2 210.8 248.7 196.9 252.0 175.5 251.3 163.5 247.8 153.8 241.6">
            <title>{{ $t('messages.app.wheel.stations.help') }}</title>
          </polygon>
          <g transform="translate(134, 247)">
            <g class="svgCircle">
              <path d="M77.1 27a23.67 23.67 0 0 1-23.67 23.67A23.67 23.67 0 0 1 29.76 27 23.67 23.67 0 0 1 53.43 3.33 23.67 23.67 0 0 1 77.1 27z"  fill="#fff"/>
            </g>
            <path d="M55.64 11.81a6 6 0 0 0-6 6c0 1.34.85 3.52 2.61 6.67 1.24 2.22 2.47 4.08 2.52 4.16l.87 1.36.9-1.35c.05-.08 1.27-1.94 2.51-4.16 1.76-3.15 2.62-5.33 2.62-6.67a6 6 0 0 0-6.03-6.01zm0 9.1a3.12 3.12 0 1 1 3.12-3.12 3.12 3.12 0 0 1-3.12 3.12z" />
            <path d="M49.35 17.44a4.17 4.17 0 0 0-4.16 4.16c0 .93.59 2.43 1.8 4.6.86 1.54 1.71 2.82 1.74 2.88l.62.93.62-.93c0-.06.88-1.34 1.73-2.88 1.22-2.17 1.81-3.67 1.81-4.6a4.17 4.17 0 0 0-4.16-4.16zm0 6.29a2.16 2.16 0 1 1 2.15-2.16 2.16 2.16 0 0 1-2.15 2.16z" />
            <path class="no-fill" d="M63.48 29.08l6.02 4.37H37.36l5.3-4.37" fill="none" stroke="#fff" stroke-width="2.74" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M48.79 34.73h9.28c.283 0 .51.227.51.51v2.47c0 .283-.227.51-.51.51h-9.28a.509.509 0 0 1-.51-.51v-2.47c0-.283.227-.51.51-.51z" fill="#fff"/>
            <path d="M48.79 34.73h9.28c.283 0 .51.227.51.51v2.47c0 .283-.227.51-.51.51h-9.28a.509.509 0 0 1-.51-.51v-2.47c0-.283.227-.51.51-.51z" fill="none" stroke="#fff" stroke-width=".84" stroke-linecap="round" stroke-linejoin="round"/>
            <path class="no-fill" d="M51.36 42.19v-6.48h4.14v6.48" id="polyline3677" fill="none" stroke="#fff" stroke-width="1.83" stroke-linecap="round" stroke-linejoin="round"/>
            <g transform="translate(10, 10)">
              <text x="43" y="55" text-anchor="middle">{{ $t('messages.app.wheel.stations.title') }}</text>
            </g>
          </g>
        </svg>
        <!-- eslint-enable -->
      </div>
      <div class="main__content">
        <router-view
          :socket-message = "SocketMessage"
          :connection-information ="ConnectionInformation"
          :controller-mode = "controllerMode"
          :selected-device = "SelectedDevice"
          :page-message = "PageMessage"
          :store="store"
          @SetPageMessage = "SetPageMessage"
          @ShowModal = "ShowModal"
        />
      </div>
    </div>
    <div
      id="Footer"
      class="Footer"
    >
      <footer-component
        :logged-in = "LoggedIn"
        :page-message = "PageMessage"
        @SetPageMessage = "SetPageMessage"
      />
    </div>
    <modal-component
      v-for="(modal, index) in modalList"
      :key="modal.component"
      :socket-message = "SocketMessage"
      :modal-data="modal"
      :page-message = "PageMessage"
      v-bind="{store, index, ConnectionInformation}"
      @SetPageMessage = "SetPageMessage"
      @CloseModal = "CloseModal"
    />

    <status-element
      v-if="LoggedIn"
      :page-message = "PageMessage"
    />
  </div>
</template>

<script>
/* Mixins */
import {SocketHandler, LifeCycle, UnitSystem} from '@/assets/javascript/mixins/Mixins'
import {
  setPrograms,
  setStations,
  setUser,
  setMain,
  setFlow,
  setElectricFlow,
  setServerTimeout,
  setSystemCapabilities
} from '@/assets/javascript/storeProcedures.js'
/* ------ */
import isEmpty from 'lodash/isEmpty'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import moment from 'moment'
import IconsLib from '@/assets/javascript/Icons-lib'

import Header from '@/components/Header'
import Footer from '@/components/Footer'
import StatusElement from '@/components/elements/StatusElement'

import RainShutDownControl from '@/components/RainShutDownControl'
import Modal from '@/components/Modal'

export default {
  name: 'App',
  components: {
    'header-component': Header,
    'footer-component': Footer,
    'modal-component': Modal,
    'font-awesome-icon': FontAwesomeIcon,
    'status-element': StatusElement,
    'rain-shut-down-control': RainShutDownControl
  },
  mixins: [SocketHandler, LifeCycle, UnitSystem],
  props: {
    store: {
      type: Object,
      default() {
        return {}
      }
    },
    PageMessage: {
      type: Object,
      default: function() {
        return{}
      }
    },
    LoggedIn: {
      type: Boolean,
      default: false
    },
    SocketMessage: {
      type: Object,
      default: function() {
        return{}
      }
    },
    SelectedDevice: {
      type: Object,
      default: function() {
        return{}
      }
    }
  },
  data() {
    return{
      timeoutInterval: null,
      clientModifiedTimeInterval: null,
      modalList: [],
      detectDataSets: false,
      /*----------------------------------------------------------------------*/
      ConnectionInformation: {},
      DashOpenClose: null,
      ApplicationPageClass: 'home',
      IsWheelShown: true,
      RouteList: [], // Gets populated in mounted, currently unused
      DoNotShowWheel: true,
      DoNotShowWheelArray: [
        '/',
        '/devicelist',
        '/admin'
      ],
      controllerMode: undefined,
      Progress: {
        progressNumber: 0,
        progressNumberModded: 100
      }
    }
  },
  computed: {
    computedAlarmList() {
      return this.store.getData('alarms.list')
    },
    computedGetPermissions() {
      return this.store.getData('user.access')
    },
    computedRainShutDown() {
      const clientModifiedTime = this.store.getData('controller.clientModifiedTime')
      const rainShutDownTime = this.store.getData('rainShutDownTime')

      if(rainShutDownTime === null) {
        this.store.setData('rainShutDownTime', clientModifiedTime)
      }

      const time = this.store.getData('rainShutDown.time') + (rainShutDownTime - clientModifiedTime) / 1000

      if(time <= 0) {
        const showSpinner = this.store.getData('rainShutDown.showRainShutDownSpinner')
        if(showSpinner === 'showStopper' || showSpinner === null) {
          this.store.setData('rainShutDown.showRainShutDownSpinner', null)
        }
        return 0
      } else {
        this.store.setData('rainShutDown.showRainShutDownSpinner', 'showBetweenStates')
        return time
      }
    },
    rainShutDownStarted() {
      return this.computedRainShutDown > 0
    },
    computedModifiedClientTime() {
      return this.store.getData('controller.clientModifiedTime')
    },
    computedTime() {
      return this.store.getData('controller.time')
    },
    collatedTabName() {
      const alarmsText = (this.computedActiveAlertsCount > 0) ? `(${this.computedActiveAlertsCount}) ` : ''
      let name = this.$i18n.whitelabel.appTitle

      if(!this.DoNotShowWheel) {
        name = `${alarmsText}${this.computedControllerName} ${name}`
      }
      return name
    },
    computedActiveAlertsCount() {
      if(this.store.data) {
        const alarms = this.store.getData('alarms')

        if(alarms && alarms.list) {
          const activeAlarms = alarms.list.filter(alarm => isNaN(parseInt(alarm.passive, 10)))
          return activeAlarms.length
        }
      }
      return 0
    },
    computedControllerName() {
      return this.store.getData('controller.name')
    },
    computedPagePathClass() {
      return this.ApplicationPageClass ? 'page-' + this.ApplicationPageClass : ''
    },
    computedWheelStatus() {
      return !this.computedGetPermissions.wheel ? 'no-wheel' : ''
    },
    computedShowOverlay() {
      const devTime = this.store.getData('controller.clientModifiedDevTime')

      if(isEmpty(this.ConnectionInformation)) {
        return !(this.DoNotShowWheelArray.indexOf(this.$route.path) !== -1)
      }

      // If we do not receive controller time, use this
      // return(!(this.ConnectionInformation.action === 'end'));

      // If we do get a time no matter what
      return !(this.ConnectionInformation.action === 'end' && !isEmpty(devTime) && this.store.getData('controller.mode') !== null)
    },
    computedSettingsTimeFormat() {
      return this.store.getData('settings.time.use12HourClock')
    }
  },
  watch: {
    computedModifiedClientTime(currentTime) {
      const timeToTimeout = this.store.getData('controller.clientTimeoutTime')

      if(timeToTimeout === null || currentTime === null) return
      const needsModal = () => parseInt(this.store.getData('controller.clientTimeoutTime') - this.computedModifiedClientTime) <= 120E3

      if(needsModal() && !this.timeoutInterval && !this.modalList.some(item => item.component === 'TimeoutModal')) {
        clearInterval(this.timeoutInterval)
        this.timeoutInterval = setInterval(() => {
          clearInterval(this.timeoutInterval)
          this.timeoutInterval = null

          if(needsModal()) this.ShowModal({
            component: 'TimeoutModal',
            data: {}
          })
        }, 5E3)
      }
    },
    computedAlarmList(alarmList) {
      if(isEmpty(alarmList) || alarmList.length === 0) {
        this.store.setData('alarms.count', 0)
        this.store.setData('alarms.maximumActiveUrgency', null)
        this.store.setData('alarms.maximumActiveUrgencyState', null)
        this.store.setData('alarms.maximumUrgency', 0)
        this.store.setData('alarms.maximumUrgencyState', null)
        return
      }

      const errorCodes = [3, 4, 5, 13]
      const warningCodes = [0, 1, 2, 6, 7, 8, 12, 14]
      const infoCodes = [9, 10, 11]
      const states = {
        1: 'green',
        2: 'yellow',
        3: 'red',
      }
      
      const getType = ID => {
        if(errorCodes.indexOf(ID) !== -1)
          return 3

        if(warningCodes.indexOf(ID) !== -1)
          return 2

        if(infoCodes.indexOf(ID) !== -1)
          return 1

        console.warn('alarm type has no match')
      }

      const getMaxUrgency = arr => Math.max.apply(Math, arr)


      let maxActiveUrgency = getMaxUrgency(alarmList.filter(alarm => (isNaN(alarm.passive) || alarm.active === alarm.passive) && !alarm.seen && !alarm.fixed).map(alarm => getType(alarm.id)))
      maxActiveUrgency = isFinite(maxActiveUrgency) ? maxActiveUrgency : 0
      const maxUrgency = getMaxUrgency(alarmList.map(alarm => getType(alarm.id)))

      this.store.setData('alarms.count', alarmList.length)
      this.store.setData('alarms.maximumActiveUrgency', maxActiveUrgency ? maxActiveUrgency : null)
      this.store.setData('alarms.maximumActiveUrgencyState', states[maxActiveUrgency] ? states[maxActiveUrgency] : null )
      this.store.setData('alarms.maximumUrgency', maxUrgency ? maxUrgency : null)
      this.store.setData('alarms.maximumUrgencyState', states[maxUrgency] ? states[maxUrgency] : null)
    },
    computedTime(time) {
      if(time === null) { // sometimes we receive bogus time
        clearInterval(this.clientModifiedTimeInterval)
        return null
      }

      this.store.setData('controller.clientModifiedTime', time)
      
      this.setClientModifiedDevTime()

      clearInterval(this.clientModifiedTimeInterval)

      this.clientModifiedTimeInterval = setInterval(() => {
        this.store.setData(
          'controller.clientModifiedTime',
          this.store.getData('controller.clientModifiedTime') + 1000
        )
        this.setClientModifiedDevTime()
      }, 1000)
    },
    computedSettingsTimeFormat() {
      const timeSettings = this.store.getData('settings.time')
      const format = (timeSettings.use12HourClock) ? timeSettings.formats.format12 : timeSettings.formats.format24
      this.store.setData('settings.time.currentFormat', format)
    },
    LoggedIn() {
      if(this.LoggedIn === false) {
        this.reset()
      }
    },
    SocketConnected(Connected) {
      if(Connected && this.$route.path !== '/') {
        this.DashOpenClose = { Source: 'Socket', Open: false }
      }
    },
    '$route'(to, from) {
      const deviceData = this.store.getData('controller.deviceData') 
      let deviceName = deviceData ? this.store.getData('controller.deviceData.devDescription') : null
      deviceName = deviceName ? `${deviceName}/` : ''

      if(!to.meta.title) console.warn('No title found in meta for this page.')
        document.title = to.meta.title !== '/'
        ? `${deviceName}${to.meta.title} ${this.$i18n.whitelabel.appTitle}`
        : this.$i18n.whitelabel.appTitle

      this.$set(this, 'DoNotShowWheel', this.DoNotShowWheelArray.indexOf(this.$route.path) !== -1)
      this.SetWheelState(to)

      if(this.DoNotShowWheel) {
        this.reset()
      }
    },

    SelectedDevice(Message) {
      this.store.setData('controller.name', this.SelectedDevice.devDescription)

      let group = this.SelectedDevice.typename.slice(0, 3)
      if(group.toLowerCase() === 'gro') group = 'TWC'

      this.store.setData('controller.group', group)
      this.store.setData('controller.deviceData', this.SelectedDevice)
    },

    SocketMessage(Message) {
      const socketData = this.socketMessageRepack(Message)

      if(socketData) {
        /**
         * For detectection of errant datasets, used for debugging
         */
        if(socketData.type === 'dataset' && socketData.data.item) {
          console.debug(socketData.data.item, Message)
        }

        /**
         * Error Handling
         */
        if(socketData.type === 'error') {
          const ignoreErrors = [
            'E02',
            'E10'
          ]

          if(!ignoreErrors.includes(socketData.data.code)) {
            console.error('error', socketData.code, socketData)
            this.$emit('SetPageMessage', {target: 'StatusElement', type: 'error', data: socketData.code})
          }
        }

        /**
         * Setting store information
         * These libs should be single purpose returns
         */
        
        switch(socketData.component) {
          case 'Programs':
            if ( socketData.data[0] && socketData.data[0].steps ) {
              // console.log('app.vue socketData', JSON.stringify(socketData.data[0].steps))
            }
            
            setPrograms(socketData, this.store)
            break

          case 'Stations':
          case 'Survey':
            setStations(socketData, this.store)
            break

          case 'ElectricFlow':
            setElectricFlow(socketData, this.store)
            break

          case 'Alarms':
            this.setAlarms(socketData.data.alarm)
            break

          case 'Flow':
            setFlow(socketData, this.store)
            break

          case 'Main':
            setMain(socketData, this.store)
            break

          case 'System':
            setSystemCapabilities(socketData, this.store)
            break
        }
        
        switch(socketData.category) {
          case 'data':
            if(socketData.data.item === 'User') setUser(socketData, this.store)

            if(socketData.data.item === 'UserPermissions') { // unused 20-08-2020
              this.store.setData('user.permissions', socketData.data.record[0])
            }
            break

          case 'device':
            if(socketData.data.type === 'datetime')
              this.store.setData('controller.time', parseInt(socketData.data.item) * 1000)
            break

          case 'Time': // 10s ticks in ms
            if(this.store.getData('controller.time')) // Sometimes we get an update before getting time
              this.store.setData('controller.time', this.store.getData('controller.time') + 10000)
            break
            
          case 'server': // time to timeout
            if(socketData.command === 'info' && socketData.code) {
              socketData.code === 'I20:0' // Likely redundant as the client clock will do the same, but here for safety.
              ? this.$router.push('/')
              : setServerTimeout(socketData, this.store)
            }
            break
        }

        if(Message && !isEmpty(socketData.data) && !isEmpty(socketData.data.code)) {
          let itemObj = IconsLib.getConnectionIcon(socketData.data.code)

          if(itemObj !== undefined) {
            this.$set(this, 'ConnectionInformation', Object.assign({}, this.ConnectionInformation, itemObj))

            if(this.ConnectionInformation !== 'end') {
              this.overlayInterval = setInterval(() => {
                clearInterval(this.overlayInterval)
                this.ConnectionInformation.action = 'end'
              }, 6000)
            }
          }
        }
      }
    },
    PageMessage() {
      if(this.PageMessage.target === 'ShowModal') {
        this.ShowModal(this.PageMessage.modalData)
      }
      if(this.PageMessage.target === 'CloseModal') {
        this.CloseModal(this.PageMessage.component)
      }

    }
  },
  mounted() {
    var fncDSs = []
    fncDSs['DecoderList'] = {}
    fncDSs['test'] = {}
    fncDSs['test2'] = {}

    // Creates a route array
    const list = Array.prototype.slice.call(document.querySelectorAll('[data-url]')) // Convert nodelist to array, map doesn't change the array so it's a const
    this.RouteList = list.map((item, index) => {
      return item.dataset.url
    })

    // Set initial wheel state
    this.SetWheelState()

    if(this.overlayInterval) clearInterval(this.overlayInterval)
    if(this.clientModifiedTimeInterval) clearInterval(this.clientModifiedTimeInterval)
  },
  destroyed() {
    if(this.overlayInterval) clearInterval(this.overlayInterval)
    if(this.clientModifiedTimeInterval) clearInterval(this.clientModifiedTimeInterval)

  },
  methods: {
    setAlarms(alarms) {
      if(alarms && alarms.length) {
        alarms = alarms.filter(alarm => !alarm.fixed).map((alarm) => {
          const formatClock = (time) => {
            return isEmpty(time)
            ? null
            : moment(parseInt(time, 10) * 1000).utc().format(this.store.getData('settings.time.currentFormat'))
          }

          const errorCodes = [3, 4, 5, 13]
          const warningCodes = [0, 1, 2, 6, 7, 8, 12, 14]
          const infoCodes = [9, 10, 11]
          const states = {
            1: 'green',
            2: 'yellow',
            3: 'red',
          }
          
          const getType = (ID) => {
            if(errorCodes.indexOf(ID) !== -1) return 3
            if(warningCodes.indexOf(ID) !== -1) return 2
            if(infoCodes.indexOf(ID) !== -1) return 1

            console.warn('alarm type has no match')
          }

          const alarmTypeToHandler = {
            'Rain': 'service-rain-alarm',
            'Alarm sensor': 'service-aux-alarm',
            'ET sensor': 'service-et-alarm',
            'Short circuit': 'service-short-alarm',
            'High Flow': 'service-high-flow-alarm',
            'Master Pump Failure': 'service-master-pump-failure-alarm',
            'Unscheduled Flow': 'service-unscheduled-flow-alarm',
            'Station Error': 'service-station-alarm',
            'Max Station': 'service-max-stations-alarm',
            'Cycle Interrupted': 'service-cycle-alarm',
            'Non-water Window, program started': 'service-n-w-w-prg-alarm',
            'Non-water Window, program stopped': 'service-n-w-w-stop-alarm',
            'Unresponsive moisture sensor': 'service-no-moist-alarm',
            'Moisture outside limits': 'service-moist-alarm',
            'Missing ET': 'service-missing-e-t-alarm'
          }

          const handler = alarmTypeToHandler[alarm.alarmType]
          const urgency = getType(parseInt(alarm.alarmID, 10))

          return {
            id: parseInt(alarm.alarmID, 10),
            label: this.$t(`messages.sharedTexts.alarms.${parseInt(alarm.alarmID, 10) + 1}`),
            urgency,
            active: parseInt(alarm.active, 10) * 1000,
            passive: parseInt(alarm.passive, 10) * 1000,
            activeDT: formatClock(alarm.active),
            passiveDT: formatClock(alarm.passive),
            state: states[urgency],
            handler,
            seen: alarm.seen,
            fixed: alarm.fixed
          }
        })

        const storeAlarms = this.store.getData('alarms.list')

        // If the alarm already exist, return the old alarm, but with new data, this is to not break the reactivity chain
        if(storeAlarms) {
          alarms = alarms.map(alarm => {
            let storeAlarm = storeAlarms.find(storeAlarm => storeAlarm.id === alarm.id)

            return storeAlarm
            ? Object.assign(storeAlarm, alarm )
            : alarm
          })
        }

        this.store.setData('alarms.list', alarms)
        return
      }
      this.store.setData('alarms.list', [])
    },
    setClientModifiedDevTime() {
      this.store.setData('controller.clientModifiedDevTime',
        moment(this.store.getData('controller.clientModifiedTime')).utc().format(this.store.getData('settings.time.currentFormat'))
      )
    },
    CloseModal(component) {
      let modalIndex = this.modalList.findIndex(modal => modal.component === component)

      modalIndex !== -1
      ? this.modalList.splice(modalIndex, 1)
      : console.warn('Modal component not found, did you uppercase component key?')

      // test locale for forcing Station list refresh trigger for new 'add station' functionality
      // works, but is *not* the correct place for this 'trigger' *TODO* find right place   -jimm 15.feb.2021
      if (this.store.getData('controller.group') === 'TWC') {
        let triggerStationsRefresh = { component: 'Stations', command: 'Mounted'}
        this.SocketMessageSend( triggerStationsRefresh )
      }
    },
    reset() {
      this.$set(this, 'modalList', [])
      this.$set(this, 'ApplicationPageClass', '')
      this.$set(this, 'ConnectionInformation', {})
      this.RemoveModalClasses()
    },
    SetPageMessage(data) {
      this.$emit('SetPageMessage', data)
    },
    RemoveModalClasses() {
      document.body.classList.remove('modal-open')
    },
    /**
      * ShowModal is parent function for showing modal
    */
    ShowModal(modalData) {
      let modalExists = this.modalList.filter(modal => modal.component === modalData.component).length
      
      if(modalExists) {
        console.warn(`ShowModal: ${modalData.component} exists, cannot have two of same type -  (likely due to nin ID requests)`)
      } else if(!isEmpty(modalData) && !isEmpty(modalData.component)) {
        this.modalList.push(modalData)
      }
    },
    /**
      * Show wheel, or not - that is the question
    */
    SetWheelState(To) {
      // Two functions are using this so we're either getting data from To or $route, probably redundant
      const Path = (To !== undefined) ? To.path : this.$route.path

      // If the route is in the no-wheel array then do not show it(login/devicelist for example)
      this.IsWheelShown = (this.DoNotShowWheelArray.indexOf(Path) === -1)

      this.ApplicationPageClass = Path.split('/')[1]
    },
    wheelClick(Evt) {
      // SVGElementInstance objects aren't normal DOM nodes, so fetch the corresponding element instead
      var target = Evt.target

      if(target.correspondingUseElement) {
        target = target.correspondingUseElement
      }

      // Start checking the intended route
      const url = target.dataset.url

      if(url === 'weather') return
      // if(url === 'history' && this.store.getData('controller.group') === 'TWC') return
      // If you're on home, do a route
      const routeToPage = () => {
        this.ApplicationPageClass = target.dataset.url
        let changeDelay = setInterval(() => {
          clearInterval(changeDelay)
          this.$router.push(url)
        }, 1000)
      }

      const routeToHome = () => {
        this.ApplicationPageClass = 'home'
        this.$router.push('home')
      }

      this.ApplicationPageClass === 'home' || this.ApplicationPageClass === ''
      ? routeToPage()
      : routeToHome()
    }
  }
}
</script>

<style lang="scss">
@import './assets/styles/global-styles.scss';

#app
{
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #001122;
  position: relative;
  margin: 0 auto;
  padding: 0;
  overflow: hidden;
  height: 100vh;
}

.applicationContent {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
  overflow: hidden;
  background-color: $color-grey-light;
  position: relative;
}

.overlay {
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: 0px;
  right:0;
  bottom: 0;
  left: 0;
  background-color: rgba($color-black, 0.75);
  z-index: $z-modal+10000;
  transition: opacity 0.5s;
  transition-delay: 1s;

  &__spinner {
    position: absolute;
    top: calc(50% + 21px);
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    @include spinner('after', 295px, 5px, $color-white);
  }

  &__text {
    position: absolute;
    top: calc(50% + 21px);
    left: 50%;
    transform: translate3d(-50%, -50%, 0) scale(0.75);
    text-align: center;
    font-size: 16px;
    color: $color-white;
    text-transform: uppercase;

    .svg-inline--fa {
      margin-bottom: 16px;
      font-size: 40px;
    }
  }

  &__devicelist {
    position: absolute;
    bottom: -55px;
    margin: 0;
    left: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    padding: 5px;
    transform: translateX(-50%);
    white-space: nowrap;

    .svg-inline--fa {
      margin: 0 5px 0 0;
      font-size: 11px;
    }
  }

  &--active {
    opacity: 1;
    pointer-events: all;
    transition: opacity 0.1s;
  }
}

.main
{
  position: relative;
  transform: translateZ(0);
  width: 100vw;
  height: 100vh;
  // height: calc(100vh - #{$FooterHeight} + #{$HeaderHeight});
  // background-image: url('@graphics/bg.svg');
  background-size: cover;
  background-color: $color-grey-light;
  background-position: bottom center;
  flex: 0px;
  overflow: hidden;

  .main__content {
    height: 100%;
    height: 100vh;
    overflow-y: scroll;
    overflow-x: hidden;
  }
}

%transforms {
  top: auto;
  right: auto;
  bottom: auto;
  left: auto;
  transform: translate3d(-50%,-50%,0) scale(0.4);
  transition-delay: 0.25s;
}

#wheel
{
  @include hardware-accelerate(false);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%,-50%,0);
  min-width: 380px;
  max-width: 620px;
  transition: all 0.6s ease-in-out;
  transition-delay: 0.25s;
  z-index: $z-ui;

  will-change: transform, top, left;

  > svg {
    width: 90vw;
    min-width: 380px;
    max-width: 620px;

    *:not(polygon) {
      pointer-events: none;
    }

    .svgCircle {
      opacity: 0.12;    
    }

    path:not(.no-fill) {
      fill: #fff
    }

    text {
      // font-family: $font-primary-semibold;
      font: normal 0.55em sans-serif;
      text-transform: uppercase;
      fill:#fff;
      opacity: 0.5;
    }  
  }

  polygon, circle, image {
    cursor: pointer;
  }

  .transitions-fast & {
    transition: all 0s ease-in-out;
  }

  .page-weather & {
    @extend %transforms;
    top: 100%;
    left: 50%;
  }

  .page-programs & {
    @extend %transforms;
    top: 50%;
    left: 0;
  }

  .page-stations &, .page-survey &, .page-sensors &, .page-maps & {
    @extend %transforms;
    top: 0;
    left: 50%;
  }

  .page-history & {
    @extend %transforms;
    top: 50%;
    left: 100%;
  }
}

@media screen and (min-width: $bp-tablet) {
  .overlay {
    &__spinner {
      top: calc(50%);
      @include spinner('after', 480px, 5px, $color-white);
    }
    &__text {
      top: calc(50%);
      transform: translate3d(-50%, -50%, 0);
    }
  }
}
</style>
